import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

interface Props {
  url: string;
  /** Basename of the downloaded PNG, without the extension. */
  fileBase: string;
  /** Pre-translated heading. */
  title: string;
  /** Pre-translated accessible name for the rendered code. */
  ariaLabel: string;
}

/**
 * A shareable link rendered as a scannable QR, with download-as-PNG and
 * copy-link actions.
 *
 * Used for both public entry points — the waiting-room counter display and
 * patient self-registration — so the strings that differ between them arrive as
 * props rather than being read from a fixed i18n namespace.
 */
export function QrCard({ url, fileBase, title, ariaLabel }: Props) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);
  // Feedback for the two actions, which are otherwise invisible: a copy that
  // silently failed and a copy that worked look identical. The download failure
  // is a distinct value, not a shared "failed" — telling someone whose PNG
  // failed to encode that they should copy the link by hand describes an action
  // they never took.
  const [status, setStatus] = useState<
    'idle' | 'copied' | 'copyFailed' | 'downloadFailed'
  >('idle');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    QRCode.toCanvas(canvas, url, { width: 240, margin: 1 }).catch((err: unknown) => {
      console.error('[QrCard] toCanvas failed', err);
      setError(t('qr.generateFailed'));
    });
  }, [url, t]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) {
        // Encoding failed. Say so rather than letting a click do nothing —
        // a silent no-op reads as a broken button.
        console.error('[QrCard] canvas.toBlob produced no blob');
        setStatus('downloadFailed');
        return;
      }
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${fileBase}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    }, 'image/png');
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setStatus('copied');
    } catch (err) {
      // The clipboard API is unavailable on insecure origins and can be denied
      // by permission policy. Recoverable — the URL is on screen either way —
      // but the user must be told, or they paste stale content into a print job
      // believing it worked.
      console.warn('[QrCard] clipboard write failed', err);
      setStatus('copyFailed');
    }
  };

  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-line bg-paper p-5">
      <div className="text-[14px] font-semibold tracking-[-0.01em] text-ink">{title}</div>
      {error ? (
        <div className="text-[12.5px] text-destructive">{error}</div>
      ) : (
        <canvas ref={canvasRef} aria-label={ariaLabel} />
      )}
      <div className="break-all text-center text-[12px] text-muted">{url}</div>
      <div className="flex gap-2">
        <Button variant="primary" size="sm" onClick={download} disabled={!!error}>
          {t('qr.downloadPng')}
        </Button>
        <Button variant="secondary" size="sm" onClick={copy}>
          {t('qr.copyLink')}
        </Button>
      </div>
      {status !== 'idle' && (
        <div
          role="status"
          aria-live="polite"
          className={
            status === 'copied'
              ? 'text-[12px] text-success'
              : 'text-[12px] text-destructive'
          }
        >
          {status === 'copied' && t('qr.copied')}
          {status === 'copyFailed' && t('qr.copyFailed')}
          {status === 'downloadFailed' && t('qr.downloadFailed')}
        </div>
      )}
    </div>
  );
}
